import { Fragment, useCallback, useEffect, useState } from 'react';
import { FaRegEnvelope, FaSyncAlt } from 'react-icons/fa';
import { PageLayout } from '../../components/layout';
import { showErrorToast } from '../../lib/ToastUtility';
import { getSiteRequests } from '../../services/SiteRequestService';
import type { SiteRequest, SiteRequestType } from '../../types/siteRequest.types';

const PAGE_SIZE = 25;

const TYPE_LABELS: Record<SiteRequestType, string> = {
  PARTNER: 'Partner',
  NEWSLETTER: 'Newsletter',
  CHANNEL_SUBSCRIPTION: 'Channel subscription',
  INDIVIDUAL_WAITLIST: 'Individual Tap2Feed waitlist',
  ORGANIZATION_WAITLIST: 'Organization Tap2Feed waitlist',
};

function formatSubmittedAt(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
}

export default function SiteRequestsPage() {
  const [requests, setRequests] = useState<SiteRequest[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [type, setType] = useState<SiteRequestType | ''>('');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const result = await getSiteRequests({
        page,
        limit: PAGE_SIZE,
        type: type || undefined,
        search: appliedSearch || undefined,
      });
      setRequests(result.requests);
      setTotal(result.total);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to load website requests.';
      setLoadError(message);
      showErrorToast(message);
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, page, type]);

  useEffect(() => {
    void loadRequests();
  }, [loadRequests]);

  const applyFilters = () => {
    setExpandedId(null);
    setPage(1);
    setAppliedSearch(search.trim());
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <PageLayout
      title="Website Requests"
      subtitle="Partner enquiries, newsletter signups, channel subscriptions, and waitlist registrations."
      actions={
        <button
          type="button"
          onClick={() => void loadRequests()}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white/80 px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-white disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100"
        >
          <FaSyncAlt className={loading ? 'animate-spin' : ''} aria-hidden="true" />
          Refresh
        </button>
      }
    >
      <div className="space-y-5 p-5 sm:p-6">
        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_240px_auto]">
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') applyFilters();
            }}
            placeholder="Search name, email, referrer, organization, channel, or message"
            maxLength={255}
            aria-label="Search website requests"
            className="min-w-0 rounded-xl border border-gray-200 bg-white/80 px-4 py-2.5 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 dark:border-white/10 dark:bg-slate-800"
          />
          <select
            value={type}
            onChange={(event) => setType(event.target.value as SiteRequestType | '')}
            aria-label="Filter by request type"
            className="rounded-xl border border-gray-200 bg-white/80 px-4 py-2.5 text-sm outline-none focus:border-emerald-500 dark:border-white/10 dark:bg-slate-800"
          >
            <option value="">All request types</option>
            {Object.entries(TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={applyFilters}
            className="rounded-xl bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-800"
          >
            Apply filters
          </button>
        </div>

        {loadError && (
          <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200">
            Request refresh failed: {loadError}
          </p>
        )}

        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
          <FaRegEnvelope aria-hidden="true" className="text-emerald-700 dark:text-emerald-400" />
          <span>{total.toLocaleString()} request{total === 1 ? '' : 's'}</span>
        </div>

        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-white/10">
          <table className="w-full min-w-[880px] border-collapse text-left text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500 dark:bg-slate-800 dark:text-gray-300">
              <tr>
                <th scope="col" className="px-4 py-3">Type</th>
                <th scope="col" className="px-4 py-3">Name / organization</th>
                <th scope="col" className="px-4 py-3">Email</th>
                <th scope="col" className="px-4 py-3">Message</th>
                <th scope="col" className="px-4 py-3">Submitted</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white/60 dark:divide-white/10 dark:bg-slate-900/30">
              {requests.map((request) => (
                <Fragment key={request.id}>
                  <tr className="align-top text-gray-700 dark:text-gray-200">
                    <td className="px-4 py-3">
                      <span className="inline-flex rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
                        {TYPE_LABELS[request.type]}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium">
                      {request.type === 'CHANNEL_SUBSCRIPTION'
                        ? request.name || '—'
                        : [request.name, request.organization].filter(Boolean).join(' / ') || '—'}
                    </td>
                    <td className="px-4 py-3">
                      <a className="text-emerald-800 underline-offset-2 hover:underline dark:text-emerald-300" href={`mailto:${request.email}`}>
                        {request.email}
                      </a>
                    </td>
                    <td className="max-w-[240px] px-4 py-3">
                      {request.message
                        ? `${request.message.slice(0, 100)}${request.message.length > 100 ? '…' : ''}`
                        : '—'}
                      {(request.phone || request.source || request.channelName || request.referrerName || request.referrerEmail || (request.message?.length ?? 0) > 100) && (
                        <button
                          type="button"
                          aria-expanded={expandedId === request.id}
                          onClick={() => setExpandedId(expandedId === request.id ? null : request.id)}
                          className="ml-2 whitespace-nowrap font-semibold text-emerald-800 hover:underline dark:text-emerald-300"
                        >
                          {expandedId === request.id ? 'Hide details' : 'Details'}
                        </button>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">{formatSubmittedAt(request.createdAt)}</td>
                  </tr>
                  {expandedId === request.id && (
                    <tr>
                      <td colSpan={5} className="bg-gray-50/80 px-5 py-4 dark:bg-slate-800/60">
                        <dl className="grid gap-3 text-sm sm:grid-cols-2">
                          <div>
                            <dt className="font-semibold text-gray-500 dark:text-gray-400">Phone</dt>
                            <dd>{request.phone || '—'}</dd>
                          </div>
                          <div>
                            <dt className="font-semibold text-gray-500 dark:text-gray-400">Source</dt>
                            <dd>{request.source || '—'}</dd>
                          </div>
                          {(request.referrerName || request.referrerEmail) && (
                            <>
                              <div>
                                <dt className="font-semibold text-gray-500 dark:text-gray-400">Referrer name</dt>
                                <dd>{request.referrerName || '—'}</dd>
                              </div>
                              <div>
                                <dt className="font-semibold text-gray-500 dark:text-gray-400">Referrer email</dt>
                                <dd>{request.referrerEmail || '—'}</dd>
                              </div>
                            </>
                          )}
                          {request.channelName && (
                            <div>
                              <dt className="font-semibold text-gray-500 dark:text-gray-400">Channel</dt>
                              <dd>{request.channelName}</dd>
                            </div>
                          )}
                          {request.message && (
                            <div className="sm:col-span-2">
                              <dt className="font-semibold text-gray-500 dark:text-gray-400">Full message</dt>
                              <dd className="whitespace-pre-wrap break-words">{request.message}</dd>
                            </div>
                          )}
                        </dl>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>

          {!loading && requests.length === 0 && (
            <div className="px-6 py-12 text-center text-sm text-gray-600 dark:text-gray-300">
              {loadError || 'No website requests match these filters.'}
            </div>
          )}
          {loading && (
            <div className="px-6 py-8 text-center text-sm text-gray-600 dark:text-gray-300">
              Loading website requests…
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 text-sm text-gray-600 dark:text-gray-300">
          <span>Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={page <= 1 || loading}
              className="rounded-lg border border-gray-200 px-3 py-2 font-medium disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              disabled={page >= totalPages || loading}
              className="rounded-lg border border-gray-200 px-3 py-2 font-medium disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
